import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { createPalette, intlPluralizeString, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Alert, ListSkeleton } from '@gitbook/spine-react';

import { NavigationLink, useNavigation } from '../../../navigation';
import { apiQuery } from '../../../state';
import { useSpaceSites } from '../../Organization/content';
import { SiteContentIcon } from '../../Site/SiteIcon';
import { getSiteTitle } from '../../Site/title';
import type { ShareContentModalProps } from '../types';
import { InviteMembersInput } from './InviteMembersInput';

const ContentPermissionsList = React.lazy(() => import('./ContentPermissionsList'));
const OrganizationInviteLinks = React.lazy(() => import('./OrganizationInviteLinks'));

/**
 * Modal for the permissions for a given content.
 */
export function InviteMembers(props: ShareContentModalProps): React.ReactElement {
    const { content, organization } = props;
    return (
        <div className="flex flex-col gap-6 divide-y">
            <div className="flex flex-col gap-6">
                <InviteMembersInput {...props} />
                <div className="flex flex-col gap-4">
                    <div className="text-muted text-ui-small">Who has access</div>
                    <React.Suspense fallback={<ListSkeleton items={3} />}>
                        {'space' in content ? (
                            <SiteAccessWarningForSpace
                                space={content.space}
                                organization={organization}
                            />
                        ) : null}
                        <ContentPermissionsList content={content} />
                    </React.Suspense>
                </div>
            </div>
            <React.Suspense fallback={null}>
                <OrganizationInviteLinks {...props} />
            </React.Suspense>
        </div>
    );
}

/**
 * Shows a warning alert if the space is part of a site and default permission is null.
 */
function SiteAccessWarningForSpace(props: {
    space: api.Space;
    organization: api.Organization;
}): React.ReactElement | null {
    const { space, organization } = props;

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', { organizationId: organization.id })
    );

    const spaceSites = useSpaceSites(organizationContent ?? null, space.id);
    if (spaceSites.length === 0) {
        return null;
    }

    const defaultLevel = space.defaultLevel;
    const resolvedDefaultPermission =
        defaultLevel === 'inherit' ? (organization.defaultRole ?? null) : defaultLevel;

    if (resolvedDefaultPermission !== null) {
        return null;
    }

    return (
        <Alert
            kind="info"
            size="xsmall"
            icon={Icon.Info}
            className="mt-2"
            details={
                <>
                    <p>
                        <LinkedSitesTitle spaceSites={spaceSites} />
                    </p>
                    Anyone who can access the{' '}
                    {intlPluralizeString({
                        count: spaceSites.length,
                        singular: 'site',
                        plural: 'sites',
                    })}{' '}
                    will automatically have <strong>Reader</strong> access to this space too.
                </>
            }
        />
    );
}

function LinkedSitesTitle(props: { spaceSites: ReturnType<typeof useSpaceSites> }) {
    const { spaceSites } = props;
    const { navigate } = useNavigation();

    const siteEntries = React.useMemo(
        () =>
            spaceSites.map(({ site }) => ({
                site,
                title: getSiteTitle(site.title),
            })),
        [spaceSites]
    );

    const linkedSitesPalette = React.useCallback(
        () =>
            createPalette({
                groups: [
                    {
                        key: 'linked-sites',
                        commands: siteEntries.map(({ site, title }) => ({
                            key: site.id,
                            title,
                            icon: <SiteContentIcon site={site} />,
                            trigger: () =>
                                navigate({
                                    screen: { type: 'site', site: site.id, sideSheet: undefined },
                                }),
                        })),
                    },
                ],
            }),
        [siteEntries, navigate]
    );

    if (siteEntries.length === 0) {
        return null;
    }

    return (
        <strong>
            {siteEntries.length > 1 ? (
                <>
                    This space is part of{' '}
                    <PaletteButton
                        kind="link"
                        className="!inline-flex !h-auto items-baseline text-primary"
                        label="multiple sites"
                        palette={linkedSitesPalette}
                    />
                </>
            ) : (
                <>
                    This space is part of the{' '}
                    <NavigationLink
                        key={siteEntries[0].site.id}
                        to={{
                            screen: {
                                type: 'site',
                                site: siteEntries[0].site.id,
                                sideSheet: undefined,
                            },
                        }}
                        className="text-ui-action"
                    >
                        {siteEntries[0].title}
                    </NavigationLink>{' '}
                    site
                </>
            )}
        </strong>
    );
}
